/* crm-collaboration.jsx — Collaboration / Discuss tab Phase 2 (Enterprise)
   Provides: MentionSuggestions, ComposerBox, AttachmentChip, CommentsPanel, FollowersPanel,
             TeamDiscussionPanel, CollaborationView
   Phase 2 features: threaded replies, file attachments, pagination, smart polling */

const { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } = React;

/* ── Inject spin keyframe for loading indicators (idempotent) ── */
if (typeof document !== 'undefined' && !document.getElementById('nx-collab-styles')) {
  const _st = document.createElement('style');
  _st.id = 'nx-collab-styles';
  _st.textContent = [
    '@keyframes nx-spin { to { transform: rotate(360deg); } }',
    '@keyframes nx-comment-flash { 0%,100% { background: transparent; } 30% { background: var(--nx-accent-tint); } }',
    '.nx-comment-highlight { border-radius: 8px; animation: nx-comment-flash 2s ease-in-out forwards; }',
  ].join('\n');
  document.head.appendChild(_st);
}

/* ── Formatting helpers ── */
const _fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d    = new Date(iso);
    const diff = Date.now() - d;
    if (diff < 60000)     return 'Just now';
    if (diff < 3600000)   return Math.floor(diff / 60000) + 'm ago';
    if (diff < 86400000)  return Math.floor(diff / 3600000) + 'h ago';
    if (diff < 604800000) return Math.floor(diff / 86400000) + 'd ago';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return ''; }
};

const _initials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const raw   = parts.length >= 2 ? parts[0][0] + parts[parts.length - 1][0] : name[0];
  return raw.toUpperCase();
};

const AVATAR_COLORS = ['#125AB0','#0E8C5A','#B5740E','#9333EA','#DE3217','#0EA5E9','#EC4899'];
const _avatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + (name.codePointAt(i) ?? 0)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

const _fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024)    return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
};

const _fileIcon = (mime) => {
  if (!mime) return 'Paperclip';
  if (mime.startsWith('image/'))                                         return 'Image';
  if (mime === 'application/pdf')                                        return 'FileText';
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv')) return 'Table';
  if (mime.includes('word') || mime.includes('document'))                return 'FileType';
  return 'File';
};

/* Encodes uploaded document metadata into a storable string */
const _encodeAtt = (d) =>
  JSON.stringify({ id: d.id, name: d.fileName || d.name || 'file', mime: d.mimeType || '', size: d.fileSize || d.size || 0 });

/* Decodes a stored attachment string; falls back gracefully for raw UUIDs */
const _decodeAtt = (s) => {
  try { return JSON.parse(s); } catch { return { id: s, name: 'attachment', mime: '', size: 0 }; }
};

/* Derives the recordType string from the type prop */
const _recordType = (type) => {
  if (!type) return 'deals';
  const t = type.toLowerCase();
  if (t === 'companies' || t === 'company') return 'companies';
  if (t === 'people'    || t === 'person')  return 'people';
  if (t === 'tasks'     || t === 'task')    return 'tasks';
  return 'deals';
};

/* Derives the correct document entity FK for upload from the type prop */
const _docMeta = (type, recordId) => {
  if (!type || !recordId) return {};
  const t = type.toLowerCase();
  if (t === 'companies' || t === 'company') return { companyId: recordId };
  if (t === 'people'    || t === 'person')  return { personId:  recordId };
  if (t === 'deals'     || t === 'deal')    return { dealId:    recordId };
  if (t === 'tasks'     || t === 'task')    return { taskId:    recordId };
  return {};
};

/* ── InlineAvatar ── */
const InlineAvatar = ({ name, size = 28 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%', flexShrink: 0,
    background: _avatarColor(name), color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: Math.round(size * 0.38), fontWeight: 700, letterSpacing: 0.5,
    userSelect: 'none',
  }}>
    {_initials(name)}
  </div>
);

/* ── AttachmentChip ── */
const AttachmentChip = ({ att, wsId, onRemove }) => {
  const handleDownload = async () => {
    try { await window.__nx_api?.document?.download?.(wsId, att.id, att.name, false); } catch {}
  };
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 8px', borderRadius: 6, maxWidth: 220,
      background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-divider)',
      fontSize: 12, color: 'var(--nx-text)',
    }}>
      <NexusIcon name={_fileIcon(att.mime)} size={12} style={{ flexShrink: 0, color: 'var(--nx-text-faint)' }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={att.name}>
        {att.name}
      </span>
      {att.size > 0 && (
        <span style={{ color: 'var(--nx-text-faint)', fontSize: 10, flexShrink: 0 }}>{_fmtSize(att.size)}</span>
      )}
      {onRemove ? (
        <button
          type="button"
          onClick={() => onRemove(att.id)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--nx-text-faint)', display: 'flex', alignItems: 'center' }}
        >
          <NexusIcon name="X" size={10} />
        </button>
      ) : (
        <button
          type="button"
          onClick={handleDownload}
          title="Download"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--nx-accent)', display: 'flex', alignItems: 'center' }}
        >
          <NexusIcon name="Download" size={11} />
        </button>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   MENTION SUGGESTIONS
   ══════════════════════════════════════════════════════════════ */
const MentionSuggestions = ({ query, onSelect, position }) => {
  const store           = useStore();
  const [focused, setFocused] = useState(0);

  const members = useMemo(() => {
    const all         = store?.workspaceMembers || [];
    const currentUser = window.__nx_auth?.user?.name;
    const fallback    = currentUser ? [{ id: 'me', name: currentUser }] : [];
    /* Normalise: store shape is { name, userId } — map userId → id so handleMentionSelect
       can reliably read m.id when populating mentionedIds for the backend payload. */
    const base = (all.length ? all : fallback).map(m => ({ ...m, id: m.id ?? m.userId }));
    const q    = (query || '').toLowerCase();
    return base.filter(m => !q || (m.name || '').toLowerCase().includes(q)).slice(0, 10);
  }, [store, query]);

  useEffect(() => { setFocused(0); }, [query]);

  useEffect(() => {
    const onKey = (e) => {
      if (!members.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setFocused(f => Math.min(f + 1, members.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setFocused(f => Math.max(f - 1, 0)); }
      if (e.key === 'Enter')     { e.preventDefault(); if (members[focused]) onSelect(members[focused]); }
      if (e.key === 'Escape')    { onSelect(null); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [members, focused, onSelect]);

  if (!members.length) return null;

  const posStyle = position?.bottom != null
    ? { bottom: position.bottom, left: position.left ?? 0, top: 'auto' }
    : { top: position?.top ?? 'auto', left: position?.left ?? 0 };

  return (
    <div
      role="menu"
      aria-label="Mention suggestions"
      style={{
        position: 'absolute', zIndex: 9999,
        background: 'var(--nx-surface)', border: '1px solid var(--nx-divider)',
        borderRadius: 8, boxShadow: 'var(--nx-shadow-lg)',
        minWidth: 200, overflow: 'hidden',
        ...posStyle,
      }}
    >
      {members.map((m, i) => (
        <div
          key={m.id || m.name}
          role="menuitem"
          tabIndex={-1}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', cursor: 'pointer',
            background: i === focused ? 'var(--nx-accent-tint-2)' : 'transparent',
            transition: 'background 0.1s',
          }}
          onMouseEnter={() => setFocused(i)}
          onMouseDown={e => { e.preventDefault(); onSelect(m); }}
        >
          <InlineAvatar name={m.name} size={22} />
          <span style={{ fontSize: 13, fontWeight: i === focused ? 600 : 400, color: 'var(--nx-text)' }}>
            {m.name}
          </span>
        </div>
      ))}
    </div>
  );
};
window.MentionSuggestions = MentionSuggestions;

/* ══════════════════════════════════════════════════════════════
   COMPOSER BOX — shared by main composer and reply forms
   onPost({ body, mentions, attachmentIds }) → undefined on success, false on error
   ══════════════════════════════════════════════════════════════ */
const ComposerBox = ({ placeholder, onPost, onCancel, wsId, record, type }) => {
  const toast        = useToast();
  const [text,        setText]        = useState('');
  const [mention,     setMention]     = useState(null);
  const [mentionedIds,setMentionedIds]= useState([]);
  const [pendingAtts, setPendingAtts] = useState([]);
  const [uploading,   setUploading]   = useState(false);
  const [submitting,  setSubmitting]  = useState(false);

  const textareaRef  = useRef(null);
  const fileInputRef = useRef(null);

  const autoResize = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  };

  const handleTextChange = (e) => {
    const val    = e.target.value;
    const cursor = e.target.selectionStart;
    autoResize(e.target);
    setText(val);
    const before = val.slice(0, cursor);
    const atIdx  = before.lastIndexOf('@');
    if (atIdx !== -1 && !before.slice(atIdx + 1).includes(' ')) {
      setMention({ query: before.slice(atIdx + 1) });
    } else {
      setMention(null);
    }
  };

  const handleMentionSelect = useCallback((m) => {
    if (!m) { setMention(null); return; }
    const cursor = textareaRef.current?.selectionStart ?? text.length;
    const before = text.slice(0, cursor);
    const atIdx  = before.lastIndexOf('@');
    const after  = text.slice(cursor);
    setText(before.slice(0, atIdx) + '@' + m.name + ' ' + after);
    setMention(null);
    if (m.id && m.id !== 'me') setMentionedIds(prev => [...new Set([...prev, m.id])]);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, [text]);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || !wsId) return;
    setUploading(true);
    const meta = _docMeta(type, record?.id);
    try {
      const uploads = await Promise.all(
        files.map(f => window.__nx_api?.document?.upload?.(wsId, f, meta)),
      );
      const encoded = uploads.filter(Boolean).map(_encodeAtt);
      setPendingAtts(prev => [...prev, ...encoded]);
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to upload file. Please try again.' });
    } finally { setUploading(false); }
  };

  const handleRemoveAtt = (id) => {
    setPendingAtts(prev => prev.filter(s => _decodeAtt(s).id !== id));
  };

  const handleSubmit = async () => {
    if (submitting || uploading) return;
    const trimmed = text.trim();
    /* Allow post when there is text OR at least one attachment */
    if (!trimmed && !pendingAtts.length) return;
    setSubmitting(true);
    try {
      const result = await onPost({ body: trimmed, mentions: mentionedIds, attachmentIds: pendingAtts });
      if (result !== false) {
        setText('');
        setMention(null);
        setMentionedIds([]);
        setPendingAtts([]);
        /* Reset textarea to default height and return focus so the user can type immediately */
        if (textareaRef.current) textareaRef.current.style.height = '';
        setTimeout(() => textareaRef.current?.focus(), 0);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isReply  = !!onCancel;
  const canSend  = !submitting && !uploading && (!!text.trim() || pendingAtts.length > 0);
  let   sendLabel = isReply ? 'Reply' : 'Post';
  if (uploading)  sendLabel = 'Uploading…';
  if (submitting) sendLabel = 'Posting…';

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 6 }}>
      {mention && (
        <MentionSuggestions
          query={mention.query}
          onSelect={handleMentionSelect}
          position={{ bottom: 'calc(100% + 4px)', left: 0 }}
        />
      )}
      <textarea
        ref={textareaRef}
        className="nx-input"
        rows={2}
        style={{
          width: '100%', resize: 'none', fontFamily: 'inherit', fontSize: 13,
          boxSizing: 'border-box', height: 'auto', minHeight: 64, maxHeight: 200,
          padding: '8px 10px', overflowY: 'auto', lineHeight: 1.55,
        }}
        value={text}
        onChange={handleTextChange}
        placeholder={placeholder || 'Write a comment… (@ to mention, Ctrl/⌘+Enter to send)'}
        onKeyDown={e => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSubmit(); }
          if (e.key === 'Escape' && onCancel) { e.preventDefault(); onCancel(); }
        }}
        disabled={submitting}
      />
      {pendingAtts.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {pendingAtts.map(s => {
            const a = _decodeAtt(s);
            return <AttachmentChip key={a.id} att={a} wsId={wsId} onRemove={handleRemoveAtt} />;
          })}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button
            type="button"
            className="nx-iconbtn"
            title={uploading ? 'Uploading…' : 'Attach file'}
            disabled={uploading || submitting}
            onClick={() => fileInputRef.current?.click()}
            style={{ color: 'var(--nx-text-faint)' }}
          >
            <NexusIcon name={uploading ? 'Loader' : 'Paperclip'} size={14} style={uploading ? { animation: 'nx-spin 0.8s linear infinite' } : undefined} />
          </button>
          {isReply && (
            <button type="button" className="nx-btn nx-btn-ghost nx-btn-sm" onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>
        <button
          type="button"
          className="nx-btn nx-btn-primary nx-btn-sm"
          onClick={handleSubmit}
          disabled={!canSend}
        >
          <NexusIcon name="Send" size={12} style={{ marginRight: 4 }} />
          {sendLabel}
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv,.doc,.docx,.xlsx,.zip"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </div>
  );
};
window.ComposerBox = ComposerBox;

/* ══════════════════════════════════════════════════════════════
   COMMENTS PANEL
   ══════════════════════════════════════════════════════════════ */
const CommentsPanel = ({ record, type, scrollToId, onConsumeScrollTo }) => {
  const wsId    = window.__nx_auth?.user?.activeWorkspaceId;
  const me      = window.__nx_auth?.user?.name || 'Me';
  const meId    = window.__nx_auth?.user?.id;
  const toast   = useToast();
  const recType = _recordType(type);

  const [comments,     setComments]     = useState([]);
  const [hasMore,      setHasMore]      = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [loadError,    setLoadError]    = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [editId,       setEditId]       = useState(null);
  const [editText,     setEditText]     = useState('');
  const [editPosting,  setEditPosting]  = useState(false);
  const [deleteId,     setDeleteId]     = useState(null);
  const [replyToId,    setReplyToId]    = useState(null);
  const [collapsedSet, setCollapsedSet] = useState(new Set());
  const [hoveredRowId, setHoveredRowId] = useState(null);

  const listRef      = useRef(null);
  const prevScrollH  = useRef(0);       // captured before loadOlder for scroll preservation
  const pollRef      = useRef(null);
  const latestCurRef = useRef(null);    // ISO string of the newest comment we hold

  /* ── Comment tree: group replies under their parents ── */
  const grouped = useMemo(() => {
    const byId = {};
    for (const c of comments) byId[c.id] = { ...c, _replies: [] };
    const tops = [];
    for (const c of comments) {
      if (c.parentId && byId[c.parentId]) byId[c.parentId]._replies.push(byId[c.id]);
      else tops.push(byId[c.id]);
    }
    return tops;
  }, [comments]);

  /* ── Initial load (latest 50 top-level + their replies) ── */
  const load = useCallback(async () => {
    if (!wsId || !record?.id) return;
    setLoading(true);
    setLoadError(false);
    try {
      const res   = await window.__nx_api?.collaboration?.listComments(wsId, record.id, { take: 50 });
      const items = Array.isArray(res) ? res : (res?.data ?? []);
      setComments(items);
      setHasMore(Array.isArray(res) ? false : (res?.hasMore ?? false));
      if (items.length) latestCurRef.current = items[items.length - 1].createdAt;
    } catch {
      setComments([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [wsId, record?.id]);

  /* ── Load older (prepend, preserve scroll position) ── */
  const loadOlder = useCallback(async () => {
    if (!wsId || !record?.id || !comments.length || loadingOlder) return;
    prevScrollH.current = listRef.current?.scrollHeight ?? 0;
    setLoadingOlder(true);
    try {
      const before = comments[0].createdAt;
      const res    = await window.__nx_api?.collaboration?.listComments(wsId, record.id, { take: 50, before });
      const items  = Array.isArray(res) ? res : (res?.data ?? []);
      if (items.length) {
        setComments(prev => [...items, ...prev]);
        setHasMore(Array.isArray(res) ? false : (res?.hasMore ?? false));
      } else {
        setHasMore(false);
      }
    } catch { /* ignore */ }
    finally { setLoadingOlder(false); }
  }, [wsId, record?.id, comments, loadingOlder]);

  /* ── Poll: fetch only new comments since our latest known timestamp ── */
  const loadNew = useCallback(async () => {
    if (!wsId || !record?.id) return;
    try {
      const since = latestCurRef.current;
      const res   = await window.__nx_api?.collaboration?.listComments(
        wsId, record.id,
        since ? { since } : { take: 50 },
      );
      const items = Array.isArray(res) ? res : (res?.data ?? []);
      if (!items.length) return;
      setComments(prev => {
        const seen  = new Set(prev.map(c => c.id));
        const fresh = items.filter(c => !seen.has(c.id));
        if (!fresh.length) return prev;
        latestCurRef.current = fresh[fresh.length - 1].createdAt;
        return [...prev, ...fresh];
      });
    } catch { /* silent — next poll will retry */ }
  }, [wsId, record?.id]);

  /* Initial load */
  useEffect(() => { load(); }, [load]);

  /* Auto-scroll to bottom on first load */
  useEffect(() => {
    if (!loading && comments.length && listRef.current && !hasMore) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [loading]); /* intentionally only after loading transitions */

  /* Scroll-position preservation after loadOlder prepends items */
  useLayoutEffect(() => {
    if (!loadingOlder && prevScrollH.current && listRef.current) {
      listRef.current.scrollTop += listRef.current.scrollHeight - prevScrollH.current;
      prevScrollH.current = 0;
    }
  }, [loadingOlder, comments.length]);

  /* Scroll the reply composer into view when it opens */
  useEffect(() => {
    if (!replyToId || !listRef.current) return;
    const timer = setTimeout(() => {
      const el = listRef.current?.querySelector(`[data-reply-for="${replyToId}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 60);
    return () => clearTimeout(timer);
  }, [replyToId]);

  /* Scroll to a specific comment (from notification click) and briefly highlight it */
  useEffect(() => {
    if (!scrollToId || loading) return;
    const attempt = () => {
      const el = document.getElementById(`nx-c-${scrollToId}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('nx-comment-highlight');
      const t = setTimeout(() => {
        el.classList.remove('nx-comment-highlight');
        onConsumeScrollTo?.();
      }, 2200);
      return t;
    };
    // Give the list one frame to render before trying to find the element
    const frame = requestAnimationFrame(() => {
      const t = attempt();
      if (!t) {
        // Element not yet in DOM — retry once after 400ms (comment may still be loading)
        const retry = setTimeout(attempt, 400);
        return () => clearTimeout(retry);
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [scrollToId, loading]);

  /* Smart polling — only while the tab is visible */
  useEffect(() => {
    const startPoll = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(loadNew, 30_000);
    };
    const stopPoll = () => {
      clearInterval(pollRef.current);
      pollRef.current = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadNew();   /* immediate catch-up on tab focus */
        startPoll();
      } else {
        stopPoll();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    if (document.visibilityState === 'visible') startPoll();
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stopPoll();
    };
  }, [loadNew]);

  /* ── Guards ── */
  const isOwn = (c) => {
    if (!meId) return false;
    return c.authorId === meId || c.authorName === me || c.author?.id === meId;
  };

  /* ── Handlers ── */
  const handleEdit = async (cId) => {
    const trimmed = editText.trim();
    if (!trimmed || editPosting) return;
    setEditPosting(true);
    try {
      await window.__nx_api?.collaboration?.editComment(wsId, record.id, cId, trimmed);
      /* Optimistically reflect the new body — loadNew() only fetches by createdAt > since,
         so it would never return an edited comment with the same createdAt */
      const now = new Date().toISOString();
      setComments(prev => prev.map(c => c.id === cId ? { ...c, body: trimmed, editedAt: now } : c));
      setEditId(null);
      setEditText('');
      await loadNew();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to edit comment' });
      setEditId(null);
      setEditText('');
      await loadNew();
    } finally {
      setEditPosting(false);
    }
  };

  const handleDelete = async (cId) => {
    try {
      await window.__nx_api?.collaboration?.deleteComment(wsId, record.id, cId);
      setDeleteId(null);
      /* Remove the deleted comment AND all its direct replies so no orphans appear as top-level */
      setComments(prev => prev.filter(c => c.id !== cId && c.parentId !== cId));
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to delete comment' });
    }
  };

  const handleMainPost = async ({ body, mentions, attachmentIds }) => {
    try {
      await window.__nx_api?.collaboration?.addComment(wsId, record.id, {
        body, recordType: recType, mentions, attachmentIds,
      });
      await loadNew();
      setTimeout(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
      }, 50);
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to post comment' });
      return false;
    }
  };

  const handleReplyPost = async (parentId, { body, mentions, attachmentIds }) => {
    try {
      await window.__nx_api?.collaboration?.addComment(wsId, record.id, {
        body, recordType: recType, mentions, attachmentIds, parentId,
      });
      setReplyToId(null);
      /* Ensure the thread stays expanded so the new reply is immediately visible */
      setCollapsedSet(prev => { const s = new Set(prev); s.delete(parentId); return s; });
      await loadNew();
    } catch (e) {
      toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to post reply' });
      return false;
    }
  };

  const toggleThread = (id) => {
    setCollapsedSet(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  /* ── Render helpers ── */
  const renderAttachments = (c) => {
    if (!c.attachmentIds?.length) return null;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
        {c.attachmentIds.map(s => {
          const a = _decodeAtt(s);
          return <AttachmentChip key={a.id} att={a} wsId={wsId} />;
        })}
      </div>
    );
  };

  const renderCommentBody = (c) => {
    if (editId === c.id) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <textarea
            className="nx-input"
            rows={2}
            style={{
              width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13,
              boxSizing: 'border-box', height: 'auto', minHeight: 64,
              padding: '8px 10px', lineHeight: 1.55,
            }}
            value={editText}
            onChange={e => setEditText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleEdit(c.id); }
              if (e.key === 'Escape') { setEditId(null); setEditText(''); }
            }}
            autoFocus
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="nx-btn nx-btn-ghost nx-btn-sm" onClick={() => { setEditId(null); setEditText(''); }}>
              Cancel
            </button>
            <button
              type="button" className="nx-btn nx-btn-primary nx-btn-sm"
              onClick={() => handleEdit(c.id)}
              disabled={!editText.trim() || editPosting}
            >
              {editPosting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      );
    }

    if (deleteId === c.id) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 13, color: 'var(--nx-text-muted)', fontStyle: 'italic', lineHeight: 1.5 }}>
            Delete this comment? This cannot be undone.
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="nx-btn nx-btn-ghost nx-btn-sm" onClick={() => setDeleteId(null)}>Cancel</button>
            <button type="button" className="nx-btn nx-btn-danger nx-btn-sm" onClick={() => handleDelete(c.id)}>
              <NexusIcon name="Trash2" size={11} style={{ marginRight: 4 }} />Delete
            </button>
          </div>
        </div>
      );
    }

    return (
      <>
        {c.body && (
          <div style={{ fontSize: 13, color: 'var(--nx-text)', lineHeight: 1.65, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
            {c.body}
          </div>
        )}
        {renderAttachments(c)}
      </>
    );
  };

  /* Single comment row — used for both top-level and replies */
  const renderCommentRow = (c, isReply = false) => {
    const author     = c.authorName || c.author?.name || 'Unknown';
    const own        = isOwn(c);
    const isDeleting = deleteId === c.id;
    const isEditing  = editId === c.id;
    const isHovered  = hoveredRowId === c.id;
    const rowBg      = isHovered ? 'var(--nx-accent-tint-2)' : 'transparent';

    return (
      <div
        key={c.id}
        id={`nx-c-${c.id}`}
        onMouseEnter={() => setHoveredRowId(c.id)}
        onMouseLeave={() => setHoveredRowId(null)}
        style={{
          display: 'flex', gap: 10,
          padding: '8px',
          marginLeft: isReply ? 36 : 0,
          paddingLeft: isReply ? 10 : 8,
          borderLeft: isReply ? '2px solid var(--nx-divider)' : 'none',
          borderRadius: 8,
          background: isDeleting ? 'rgba(222,50,23,0.06)' : rowBg,
          transition: 'background 0.12s',
        }}
      >
        <InlineAvatar name={author} size={isReply ? 22 : 28} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap', marginBottom: 3 }}>
            <span style={{ fontWeight: 700, fontSize: isReply ? 12 : 13, color: 'var(--nx-text)' }}>{author}</span>
            <span
              style={{ fontSize: 11, color: 'var(--nx-text-faint)', cursor: 'default' }}
              title={c.createdAt ? new Date(c.createdAt).toLocaleString() : ''}
            >
              {_fmtTime(c.createdAt)}
            </span>
            {c.editedAt && (
              <span
                style={{ fontSize: 10, color: 'var(--nx-text-faint)', fontStyle: 'italic', cursor: 'default' }}
                title={'Edited ' + new Date(c.editedAt).toLocaleString()}
              >
                edited
              </span>
            )}
          </div>
          {renderCommentBody(c)}
          {/* Reply button — only on top-level comments, not while editing/deleting */}
          {!isReply && !isEditing && !isDeleting && (
            <button
              type="button"
              onClick={() => {
                if (replyToId !== c.id) {
                  setCollapsedSet(prev => { const s = new Set(prev); s.delete(c.id); return s; });
                }
                setReplyToId(replyToId === c.id ? null : c.id);
              }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: '3px 0',
                fontSize: 11,
                color: isHovered ? 'var(--nx-accent)' : 'var(--nx-text-faint)',
                fontWeight: 600, marginTop: 2,
                transition: 'color 0.12s',
              }}
            >
              {replyToId === c.id ? 'Cancel reply' : 'Reply'}
            </button>
          )}
        </div>
        {/* Edit / Delete only visible on hover to keep conversations readable */}
        {own && !isEditing && !isDeleting && isHovered && (
          <div style={{ display: 'flex', gap: 2, flexShrink: 0, alignSelf: 'flex-start', paddingTop: 2 }}>
            <button
              type="button" className="nx-iconbtn" title="Edit"
              onClick={() => { setEditId(c.id); setEditText(c.body); setDeleteId(null); setReplyToId(null); }}
              style={{ color: 'var(--nx-text-faint)' }}
            >
              <NexusIcon name="Pencil" size={12} />
            </button>
            <button
              type="button" className="nx-iconbtn" title="Delete"
              onClick={() => { setDeleteId(c.id); setEditId(null); setReplyToId(null); }}
              style={{ color: 'var(--nx-danger)' }}
            >
              <NexusIcon name="Trash2" size={12} />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderList = () => {
    if (loading && !comments.length) {
      return [0, 1, 2].map(i => (
        <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 0', alignItems: 'flex-start' }}>
          <div className="nx-skeleton-line" style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0 }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div className="nx-skeleton-line" style={{ width: '45%' }} />
            <div className="nx-skeleton-line" style={{ width: '80%' }} />
          </div>
        </div>
      ));
    }

    if (loadError && !comments.length) {
      return (
        <div className="nx-empty-state is-compact">
          <div className="nx-empty-state-icon"><NexusIcon name="AlertCircle" size={20} /></div>
          <div className="nx-empty-state-title">Failed to load comments</div>
          <div className="nx-empty-state-desc" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <span>Something went wrong. Please try again.</span>
            <button type="button" className="nx-btn nx-btn-ghost nx-btn-sm" onClick={load}>
              <NexusIcon name="RefreshCw" size={12} style={{ marginRight: 4 }} />Retry
            </button>
          </div>
        </div>
      );
    }

    if (!comments.length) {
      return (
        <div className="nx-empty-state is-compact">
          <div className="nx-empty-state-icon"><NexusIcon name="MessageSquare" size={20} /></div>
          <div className="nx-empty-state-title">No comments yet</div>
          <div className="nx-empty-state-desc">Start the discussion. Mention teammates with @, or attach a file.</div>
        </div>
      );
    }

    return grouped.map(c => {
      const replyCount  = c._replies.length;
      const collapsed   = collapsedSet.has(c.id);
      const replyWord   = replyCount !== 1 ? 'replies' : 'reply';
      const threadLabel = collapsed ? `Show ${replyCount} ${replyWord}` : `Hide ${replyCount} ${replyWord}`;

      return (
        <div key={c.id} style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {renderCommentRow(c, false)}

          {/* Inline reply composer for this comment */}
          {replyToId === c.id && (
            <div data-reply-for={c.id} style={{ marginLeft: 36, marginTop: 4, marginBottom: 6 }}>
              <ComposerBox
                placeholder="Write a reply… (Ctrl/⌘+Enter to send, Esc to cancel)"
                onPost={(p) => handleReplyPost(c.id, p)}
                onCancel={() => setReplyToId(null)}
                wsId={wsId}
                record={record}
                type={type}
              />
            </div>
          )}

          {/* Thread expand/collapse toggle + reply rows */}
          {replyCount > 0 && (
            <>
              <button
                type="button"
                onClick={() => toggleThread(c.id)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 4,
                  marginLeft: 46, marginTop: 1, marginBottom: 1,
                  fontSize: 11, color: 'var(--nx-accent)', fontWeight: 600, padding: '2px 0',
                  alignSelf: 'flex-start',
                }}
              >
                <NexusIcon name={collapsed ? 'ChevronRight' : 'ChevronDown'} size={11} />
                {threadLabel}
              </button>
              {!collapsed && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {c._replies.map(r => renderCommentRow(r, true))}
                </div>
              )}
            </>
          )}
        </div>
      );
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>

      {/* Comment list with optional "Load older" affordance */}
      <div
        ref={listRef}
        style={{ flex: 1, overflowY: 'auto', padding: '4px 0 8px', display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        {hasMore && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 8px' }}>
            <button
              type="button"
              className="nx-btn nx-btn-ghost nx-btn-sm"
              onClick={loadOlder}
              disabled={loadingOlder}
              style={{ fontSize: 12 }}
            >
              <NexusIcon name={loadingOlder ? 'Loader' : 'ChevronsUp'} size={12} style={{ marginRight: 4, ...(loadingOlder ? { animation: 'nx-spin 0.8s linear infinite' } : {}) }} />
              {loadingOlder ? 'Loading…' : 'Load older messages'}
            </button>
          </div>
        )}
        {renderList()}
      </div>

      {/* Main composer */}
      <div style={{ flexShrink: 0, borderTop: '1px solid var(--nx-divider)', paddingTop: 12, position: 'relative' }}>
        <ComposerBox
          placeholder="Write a comment… (@ to mention, Ctrl/⌘+Enter to send)"
          onPost={handleMainPost}
          wsId={wsId}
          record={record}
          type={type}
        />
      </div>
    </div>
  );
};
window.CommentsPanel = CommentsPanel;

/* ══════════════════════════════════════════════════════════════
   FOLLOWERS PANEL
   ══════════════════════════════════════════════════════════════ */

const _FOLLOW_LABELS = { companies: 'company', company: 'company', people: 'contact', person: 'contact', deals: 'deal' };

// Lifted to module level — keeps FollowersPanel cognitive complexity within limits
const _doToggleFollow = async ({ wsId, recordId, recType, meId, me, amFollowing, prevFollowers, setFollowers, setFollowing, load, toast }) => {
  if (!wsId || !recordId) return;

  if (amFollowing) {
    setFollowers(prev => prev.filter(f => f.userId !== meId && f.id !== meId));
  } else {
    setFollowers(prev => [...prev, { id: `opt-${meId}`, userId: meId, userName: me }]);
  }

  setFollowing(true);
  try {
    if (amFollowing) {
      await window.__nx_api?.collaboration?.unfollow(wsId, recordId);
      toast({ tone: 'success', icon: 'Check', title: "You've unfollowed this record" });
    } else {
      await window.__nx_api?.collaboration?.follow(wsId, recordId, { recordType: recType });
      toast({ tone: 'success', icon: 'Check', title: "You're now following this record" });
    }
    await load();
  } catch (e) {
    setFollowers(prevFollowers);
    toast({ tone: 'danger', icon: 'AlertCircle', title: e?.message || 'Failed to update follow status' });
  } finally {
    setFollowing(false);
  }
};

const FollowerList = ({ followers, loading, meId }) => {
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[0, 1].map(i => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="nx-skeleton-line" style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0 }} />
            <div className="nx-skeleton-line" style={{ width: `${40 + i * 15}%` }} />
          </div>
        ))}
      </div>
    );
  }

  if (!followers.length) {
    return <span style={{ fontSize: 12, color: 'var(--nx-text-faint)' }}>No followers yet.</span>;
  }

  const count = followers.length;
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 8 }}>
        {count} {count !== 1 ? 'followers' : 'follower'}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {followers.map((f, i) => {
          const name = f.userName || f.name || f.user?.name || 'Unknown';
          const isMe = f.userId === meId || f.id === meId;
          return (
            <div key={f.id || f.userId || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
              <InlineAvatar name={name} size={24} />
              <span style={{ fontSize: 13, color: 'var(--nx-text)', flex: 1 }}>{name}</span>
              {isMe && (
                <span style={{
                  fontSize: 11, color: 'var(--nx-text-muted)',
                  border: '1px solid var(--nx-divider)',
                  padding: '0 5px', borderRadius: 3, lineHeight: '16px',
                }}>
                  You
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FollowersPanel = ({ record, type }) => {
  const wsId    = window.__nx_auth?.user?.activeWorkspaceId;
  const meId    = window.__nx_auth?.user?.id;
  const me      = window.__nx_auth?.user?.name || '';
  const toast   = useToast();
  const recType = _recordType(type);

  const [followers, setFollowers] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [following, setFollowing] = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);

  const menuRef   = useRef(null);
  // Capture action direction at click time — React 18 batches the optimistic setFollowers
  // + setFollowing into one render, flipping amFollowing before the loading label reads it.
  const _actionRef = useRef(false);

  const load = useCallback(async () => {
    if (!wsId || !record?.id) return;
    setLoading(true);
    try {
      const res = await window.__nx_api?.collaboration?.listFollowers(wsId, record.id);
      setFollowers(Array.isArray(res) ? res : (res?.data ?? []));
    } catch {
      setFollowers([]);
    } finally {
      setLoading(false);
    }
  }, [wsId, record?.id]);

  useEffect(() => { load(); }, [load]);

  // Close menu on outside click or Escape
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const amFollowing = useMemo(
    () => followers.some(f => f.userId === meId || f.id === meId),
    [followers, meId],
  );

  // Follow (not yet following): fire immediately
  const handleFollow = () => {
    _actionRef.current = false;
    _doToggleFollow({ wsId, recordId: record?.id, recType, meId, me, amFollowing,
      prevFollowers: followers, setFollowers, setFollowing, load, toast });
  };

  // Unfollow: called only from the dropdown menu
  const handleUnfollow = () => {
    setMenuOpen(false);
    _actionRef.current = true;
    _doToggleFollow({ wsId, recordId: record?.id, recType, meId, me, amFollowing,
      prevFollowers: followers, setFollowers, setFollowing, load, toast });
  };

  const recLabel = _FOLLOW_LABELS[type] || 'record';

  let followBtnLabel = amFollowing ? 'Following' : 'Follow';
  if (following) followBtnLabel = _actionRef.current ? 'Unfollowing…' : 'Following…';

  let followBtnIcon = amFollowing ? 'BellRing' : 'Bell';
  if (following) followBtnIcon = 'Loader';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', padding: '2px 0' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', flex: 1 }}>Followers</span>

        {/* Button + dropdown wrapper — ref covers both so outside-click works */}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            type="button"
            className="nx-btn nx-btn-sm nx-btn-secondary"
            onClick={amFollowing ? () => setMenuOpen(prev => !prev) : handleFollow}
            disabled={following || loading}
            aria-label={amFollowing ? 'Manage follow status' : 'Follow this record'}
            aria-haspopup={amFollowing ? 'menu' : undefined}
            aria-expanded={amFollowing ? menuOpen : undefined}
          >
            <NexusIcon
              name={followBtnIcon}
              size={12}
              style={{ marginRight: 4, ...(following ? { animation: 'nx-spin 0.8s linear infinite' } : {}) }}
            />
            {followBtnLabel}
            {amFollowing && !following && (
              <NexusIcon name="ChevronDown" size={11} style={{ marginLeft: 2 }} />
            )}
          </button>

          {/* Dropdown menu — only when following and open */}
          {amFollowing && menuOpen && (
            <div
              role="menu"
              style={{
                position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 200,
                background: 'var(--nx-surface)', border: '1px solid var(--nx-border)',
                borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                minWidth: 148, padding: '4px 0',
              }}
            >
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 12px', fontSize: 13,
                color: 'var(--nx-text)', cursor: 'default', userSelect: 'none',
              }}>
                <NexusIcon name="Check" size={12} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
                Following
              </div>
              <div style={{ height: 1, background: 'var(--nx-divider)', margin: '4px 0' }} />
              <button
                type="button"
                role="menuitem"
                onClick={handleUnfollow}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  width: '100%', padding: '7px 12px',
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 13, color: 'var(--nx-text)', textAlign: 'left',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--nx-surface-alt)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
              >
                <NexusIcon name="BellOff" size={12} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
                Unfollow
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Helper text */}
      <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--nx-text-faint)', lineHeight: 1.5 }}>
        Receive notifications when this {recLabel} is updated.
      </p>

      {/* Divider */}
      <div style={{ height: 1, background: 'var(--nx-divider)', marginBottom: 12 }} />

      {/* Follower list */}
      <FollowerList followers={followers} loading={loading} meId={meId} />
    </div>
  );
};
window.FollowersPanel = FollowersPanel;

/* ══════════════════════════════════════════════════════════════
   TEAM DISCUSSION PANEL — two-tab wrapper used by the drawer
   ══════════════════════════════════════════════════════════════ */
const TeamDiscussionPanel = ({ record, type }) => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;
  const [tab, setTab] = useState('discussion');
  const [scrollToCommentId, setScrollToCommentId] = useState(null);
  const [hasUnread, setHasUnread] = useState(false);

  // Fetch unread state once on mount
  useEffect(() => {
    if (!wsId || !record?.id) return;
    window.__nx_api?.collaboration?.getDiscussionUnread?.(wsId, record.id)
      .then(r => setHasUnread(r?.hasUnread ?? false))
      .catch(() => {});
  }, [wsId, record?.id]);

  // Mark viewed whenever Discussion tab is active
  useEffect(() => {
    if (tab !== 'discussion' || !wsId || !record?.id) return;
    setHasUnread(false);
    window.__nx_api?.collaboration?.markDiscussionViewed?.(wsId, record.id).catch(() => {});
  }, [tab, wsId, record?.id]);

  // Consume a pending discuss intent (set by notification click-to-navigate)
  useEffect(() => {
    const intent = window.__nx_discuss_intent;
    if (!intent) return;
    window.__nx_discuss_intent = null;
    setTab('discussion');
    if (intent.commentId) setScrollToCommentId(intent.commentId);
  }, []);

  const TABS = [
    { key: 'discussion', label: 'Discussion', icon: 'MessageSquare', unread: hasUnread },
    { key: 'followers',  label: 'Followers',  icon: 'Users' },
  ];

  return (
    <div className="nx-drawer-page" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, padding: 0 }}>
      <div style={{ display: 'flex', borderBottom: '1px solid var(--nx-divider)', flexShrink: 0, paddingTop: 4 }}>
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '8px 14px', background: 'transparent', border: 'none',
              cursor: 'pointer', fontSize: 13,
              fontWeight: tab === t.key ? 700 : 500,
              color: tab === t.key ? 'var(--nx-accent)' : 'var(--nx-text-muted)',
              borderBottom: tab === t.key ? '2px solid var(--nx-accent)' : '2px solid transparent',
              marginBottom: -1, transition: 'color 0.15s, border-color 0.15s',
              position: 'relative',
            }}
          >
            <NexusIcon name={t.icon} size={13} />
            {t.label}
            {t.unread && tab !== t.key && (
              <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: 'var(--nx-accent)', flexShrink: 0,
                display: 'inline-block', marginLeft: 2,
              }} aria-label="unread messages" />
            )}
          </button>
        ))}
      </div>

      {/* Discussion: overflow visible so @mention dropdown escapes clipping boundary.
          CommentsPanel manages its own internal scroll via listRef.
          Followers: overflow auto so the list scrolls when it overflows. */}
      <div style={{
        flex: 1, minHeight: 0,
        overflow: tab === 'discussion' ? 'visible' : 'auto',
        display: 'flex', flexDirection: 'column',
        padding: '12px 20px',
      }}>
        {tab === 'discussion' && <CommentsPanel record={record} type={type} scrollToId={scrollToCommentId} onConsumeScrollTo={() => setScrollToCommentId(null)} />}
        {tab === 'followers'  && <FollowersPanel record={record} type={type} />}
      </div>
    </div>
  );
};
window.TeamDiscussionPanel = TeamDiscussionPanel;

/* ── CollaborationView — workspace-level standalone (not used in the drawer) ── */
const CollaborationView = () => {
  const wsId = window.__nx_ws?.id || window.__nx_ws?.workspaceId;
  if (!wsId) {
    return <div style={{ padding: 24, color: 'var(--nx-text-muted)', fontSize: 13 }}>No workspace active.</div>;
  }
  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 4, color: 'var(--nx-text)' }}>Collaboration</div>
      <div style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 20 }}>
        Workspace discussions and team activity.
      </div>
      <div style={{
        maxWidth: 800, background: 'var(--nx-surface-alt)',
        border: '1px solid var(--nx-divider)', borderRadius: 10,
        padding: '16px 20px', overflow: 'hidden',
      }}>
        <TeamDiscussionPanel record={{ id: wsId }} type="workspace" />
      </div>
    </div>
  );
};
window.CollaborationView = CollaborationView;
